import debounce from 'lodash.debounce';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ReactZoomPanPinchRef, TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import { CloudItem, EStorageType } from 'reactApp/modules/storage/storage.types';
import { PdfViewer } from 'reactApp/ui/PdfViewer/PdfViewer';
import { fetchDocumentData } from 'reactApp/ui/ReactViewer/MobilePdfViewer/MobilePdfViewer.helpers';
import { Spinner } from 'reactApp/ui/Spinner/Spinner';
import { sendXray } from 'reactApp/utils/ga';
import { noop } from 'reactApp/utils/helpers';

import styles from './MobilePdfViewer.css';

type MobilePdfViewerProps = {
    item: CloudItem;
    storage: EStorageType;
    wait?: boolean;
    onShow();
    onClose();
};

let lastTouch,
    continues,
    lastY = 0,
    lastX = 0;

const TOUCH_MIN_MOVE_DISTANCE = 50;

export const MobilePdfViewer: FC<MobilePdfViewerProps> = ({ item, storage, wait = false, onShow = noop, onClose = noop }) => {
    const ref = useRef<ReactZoomPanPinchRef>(null);
    const componentRef = useRef<HTMLDivElement>(null);
    const [scale, setScale] = useState(1);
    const [error, setError] = useState('');
    const [content, setContent] = useState<ArrayBuffer | null>(null);

    useEffect(() => {
        let lastScale = 1;
        const debounceScale = debounce((scale) => {
            setScale(scale);
        }, 200);

        const updateScale = () => {
            if (!ref.current) {
                return;
            }

            const scale = ref.current.state.scale;

            if (lastScale !== scale) {
                debounceScale(scale);
                lastScale = scale;
            }
        };

        const interval = setInterval(updateScale, 100);

        return () => {
            clearInterval(interval);
        };
    }, [ref.current]);

    useEffect(() => {
        if (!ref.current) {
            return;
        }

        const reset = () => ref.current?.resetTransform();

        (window as any).zoom = ref.current;

        window.addEventListener('resize', reset);

        return () => {
            window.removeEventListener('resize', reset);
        };
    }, [ref.current]);

    useEffect(() => {
        let cancel = false;
        if (!item || !storage || wait || content?.byteLength) {
            return;
        }

        fetchDocumentData({ item, storage })
            .then((content) => {
                if (cancel) {
                    return;
                }

                setContent(content);
            })
            .catch((error) => {
                setError(error.message);
            });

        return () => {
            cancel = true;
        };
    }, [item, storage, wait]);

    useEffect(() => {
        onShow();

        return () => {
            onClose();
        };
    }, [onShow, onClose]);

    useEffect(() => {
        sendXray(['viewer', 'pdf', 'open']);
    }, []);

    useEffect(() => {
        if (error) {
            sendXray(['viewer', 'pdf', 'error', error]);
        }
    }, [error]);

    const handleError = useCallback((error: string) => {
        if (error) {
            setError(error);
        }
    }, []);

    const handlePanningStart = useCallback((ref: ReactZoomPanPinchRef, event: TouchEvent | MouseEvent) => {
        if (!(event instanceof TouchEvent)) {
            return;
        }

        const { identifier, screenY, screenX } = event?.changedTouches?.[0] || {};
        lastTouch = identifier;
        continues = false;
        lastY = screenY;
        lastX = screenX;
    }, []);

    const handlePanningStop = useCallback(() => {
        lastTouch = undefined;
        continues = false;
        lastY = 0;
        lastX = 0;
    }, []);

    const handlePanning = useCallback(
        (ref: ReactZoomPanPinchRef, event: TouchEvent | MouseEvent) => {
            if (!componentRef.current || !(event instanceof TouchEvent)) {
                return;
            }

            const { scale, positionX, positionY } = ref.state;

            const { identifier, screenY, screenX } = event?.changedTouches?.[0] || {};
            const { scrollTop, scrollHeight, clientHeight } = componentRef.current;

            if (identifier === lastTouch && screenY && screenX) {
                const Xdif = screenX - lastX;
                const Ydif = screenY - lastY;

                const XMove = Math.abs(Xdif);
                const YMove = Math.abs(Ydif);

                if (XMove > TOUCH_MIN_MOVE_DISTANCE || YMove > TOUCH_MIN_MOVE_DISTANCE || continues) {
                    const scrollAtBeginning = scrollTop === 0;
                    const scrollAtEnd = Math.abs(scrollHeight - clientHeight - scrollTop) < 1;
                    const isMoveUp = Ydif > 0;
                    const isMoveDown = Ydif < 0;

                    if ((scrollAtBeginning && isMoveUp) || (scrollAtEnd && isMoveDown)) {
                        ref.setTransform(positionX + Xdif, positionY + Ydif, scale, 0);
                    } else {
                        ref.setTransform(positionX + Xdif, positionY, scale, 0);
                        componentRef.current.scrollTo({
                            top: scrollTop - Ydif / scale,
                        });
                    }

                    continues = true;
                    lastX = screenX;
                    lastY = screenY;
                }
            }
            if (identifier !== lastTouch) {
                lastTouch = identifier;
                lastX = screenX;
                lastY = screenY;
            }
        },
        [componentRef.current]
    );

    const wheel = useMemo(() => ({ disabled: true }), []);

    const panning = useMemo(
        () => ({
            disabled: scale <= 1,
            velocityDisabled: true,
            lockAxisY: true,
        }),
        [scale]
    );

    const doubleClick = useMemo(
        () => ({ step: 1, mode: scale <= 1 ? ('zoomIn' as const) : ('reset' as const), animationTime: 200 }),
        [scale]
    );

    return (
        <>
            {content?.byteLength && !wait && !error && (
                <TransformWrapper
                    initialScale={1}
                    maxScale={4}
                    minScale={1}
                    ref={ref}
                    wheel={wheel}
                    panning={panning}
                    onPanningStart={handlePanningStart}
                    onPanning={handlePanning}
                    onPanningStop={handlePanningStop}
                    doubleClick={doubleClick}
                >
                    <TransformComponent wrapperClass={styles.zoomWrapper} contentClass={styles.zoomContent}>
                        <div className={styles.viewerWrapper}>
                            <PdfViewer
                                zoomScale={scale}
                                saveLoaded={scale > 1}
                                ref={componentRef}
                                content={content}
                                onError={handleError}
                                mobile
                                bufferPages={2}
                            />
                        </div>
                    </TransformComponent>
                </TransformWrapper>
            )}
            {error && <div className={styles.text}>{error}</div>}
            {((!content?.byteLength && !error) || wait) && <Spinner />}
        </>
    );
};
